/**
 * Die Verwaltung — Mitglieder und Schnapskatalog pflegen.
 */
import { useState, type FormEvent } from 'react'
import { api, ApiFehler, gespeichertesMitglied, speichereSitzung } from '../api/client.js'
import { useLaden } from '../api/laden.js'
import { aktuellesThema, setzeThema, type Thema } from '../api/thema.js'
import type { Mitglied, Schnaps } from '../api/typen.js'
import {
  Abschnitt,
  Amtsknopf,
  eingabeKlasse,
  Fehlermeldung,
  Laufvermerk,
} from '../bausteine/Bausteine.js'

export function Verwaltung() {
  const ich = useLaden(() => api.get<{ host: boolean }>('/api/auth/ich'))
  const [freigeschaltet, setFreigeschaltet] = useState(false)

  if (ich.laedt) return <Laufvermerk />
  const istHost = freigeschaltet || ich.daten?.host === true

  return (
    <>
      <section className="pt-8">
        <h1 className="text-2xl">Verwaltung</h1>
        <p className="mt-2 text-sm text-akte/75">
          Mitglieder und Katalog. Gelöscht wird nichts — was ausscheidet, wird stillgelegt, damit
          die Akten lesbar bleiben.
        </p>
      </section>
      <GewandWahl />
      {!istHost ? (
        <HostAufschluss beiErfolg={() => setFreigeschaltet(true)} />
      ) : (
        <>
          <MitgliederPflege />
          <KatalogPflege />
        </>
      )}
    </>
  )
}

/**
 * Das Gewand ist Geschmackssache und gilt nur auf diesem Geraet — deshalb
 * steht es vor dem Host-Schloss.
 */
function GewandWahl() {
  const [thema, setThema] = useState<Thema>(() => aktuellesThema())

  const waehle = (neu: Thema): void => {
    setzeThema(neu)
    setThema(neu)
  }

  return (
    <Abschnitt titel="Gewand dieses Geräts">
      <div className="grid grid-cols-2 gap-2">
        {(
          [
            ['gerichtssaal', 'Gerichtssaal bei Nacht', 'Oxblood, Messing, das Pendel.'],
            ['kanzlei', 'Kanzleiakte', 'Pergament im Nussbaumdeckel, Siegellack.'],
          ] as const
        ).map(([wert, name, hinweis]) => (
          <button
            key={wert}
            type="button"
            onClick={() => waehle(wert)}
            aria-pressed={thema === wert}
            className={`border px-3 py-3 text-left transition-colors ${
              thema === wert
                ? 'border-messing bg-messing/15'
                : 'border-eiche hover:border-messing/50'
            }`}
          >
            <span className="block text-sm text-lampe">{name}</span>
            <span className="block text-xs text-akte/70">{hinweis}</span>
          </button>
        ))}
      </div>
    </Abschnitt>
  )
}

/**
 * Die Verwaltung ist dem Host vorbehalten — sonst legt jeder jeden still.
 * Das Host-Passwort schaltet ein neues Token mit Host-Rechten frei.
 */
function HostAufschluss({ beiErfolg }: { beiErfolg: () => void }) {
  const [passwort, setPasswort] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function aufschliessen(e: FormEvent) {
    e.preventDefault()
    setLaeuft(true)
    setFehler(null)
    try {
      const antwort = await api.post<{ token: string }>('/api/auth/host', { passwort })
      const mitglied = gespeichertesMitglied()
      if (mitglied) speichereSitzung(antwort.token, { ...mitglied, host: true })
      beiErfolg()
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Der Aufschluss ist fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <form onSubmit={aufschliessen} className="mt-6 flex flex-col gap-3 border border-eiche bg-bank p-4">
      <p className="protokoll">Nur für den Host</p>
      <p className="text-sm text-akte/80">
        Die Verwaltung ist mit dem Host-Passwort verschlossen. Wer es kennt, führt die Runde.
      </p>
      <input
        type="password"
        value={passwort}
        onChange={(e) => setPasswort(e.target.value)}
        className={`w-full ${eingabeKlasse}`}
        placeholder="Host-Passwort"
        autoComplete="current-password"
      />
      <Amtsknopf type="submit" disabled={laeuft || !passwort}>
        {laeuft ? 'Wird geprüft …' : 'Aufschließen'}
      </Amtsknopf>
      <Fehlermeldung text={fehler} />
    </form>
  )
}

function MitgliederPflege() {
  const mitglieder = useLaden(() => api.get<Mitglied[]>('/api/members'))
  const [name, setName] = useState('')
  const [emoji, setEmoji] = useState('🥃')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function anlegen(e: FormEvent) {
    e.preventDefault()
    setLaeuft(true)
    setFehler(null)
    try {
      await api.post('/api/members', { name, avatarEmoji: emoji })
      setName('')
      mitglieder.neuLaden()
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Aufnahme fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  async function umschalten(m: Mitglied) {
    try {
      await api.patch(`/api/members/${m.id}`, { isActive: !m.isActive })
      mitglieder.neuLaden()
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Änderung fehlgeschlagen.')
    }
  }

  if (mitglieder.laedt) return <Laufvermerk />

  return (
    <Abschnitt titel="Mitglieder">
      <ul className="flex flex-col gap-1.5">
        {(mitglieder.daten ?? []).map((m) => (
          <li
            key={m.id}
            className="flex items-center justify-between gap-3 border border-eiche bg-bank px-3.5 py-2.5"
          >
            <span className={`flex min-w-0 items-center gap-2.5 ${m.isActive ? '' : 'opacity-45'}`}>
              <span aria-hidden="true">{m.avatarEmoji}</span>
              <span className="truncate text-lampe">{m.name}</span>
            </span>
            <button
              type="button"
              onClick={() => void umschalten(m)}
              className="protokoll shrink-0 normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing"
            >
              {m.isActive ? 'Stilllegen' : 'Reaktivieren'}
            </button>
          </li>
        ))}
      </ul>

      <form onSubmit={anlegen} className="mt-3 flex items-stretch gap-2">
        <input
          value={emoji}
          onChange={(e) => setEmoji(e.target.value)}
          className={`${eingabeKlasse} w-16 shrink-0 text-center`}
          aria-label="Avatar-Emoji"
          maxLength={8}
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`w-full min-w-0 ${eingabeKlasse}`}
          placeholder="Name"
          maxLength={40}
        />
        <Amtsknopf type="submit" disabled={!name.trim() || laeuft}>
          Aufnehmen
        </Amtsknopf>
      </form>
      <Fehlermeldung text={fehler} />
    </Abschnitt>
  )
}

function KatalogPflege() {
  const katalog = useLaden(() => api.get<Schnaps[]>('/api/katalog?alle=true'))
  const [name, setName] = useState('')
  const [staerke, setStaerke] = useState('38')
  const [emoji, setEmoji] = useState('🥃')
  const [alkoholfrei, setAlkoholfrei] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function anlegen(e: FormEvent) {
    e.preventDefault()
    setLaeuft(true)
    setFehler(null)
    try {
      await api.post('/api/katalog', {
        name,
        emoji,
        strength: alkoholfrei ? 0 : Number(staerke),
        isAlcoholfree: alkoholfrei,
      })
      setName('')
      katalog.neuLaden()
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Aufnahme fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  async function umschalten(s: Schnaps) {
    try {
      await api.patch(`/api/katalog/${s.id}`, { isActive: !s.isActive })
      katalog.neuLaden()
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Änderung fehlgeschlagen.')
    }
  }

  if (katalog.laedt) return <Laufvermerk />

  return (
    <Abschnitt titel="Schnapskatalog">
      <ul className="flex flex-col gap-1.5">
        {(katalog.daten ?? []).map((s) => (
          <li
            key={s.id}
            className="flex items-center justify-between gap-3 border border-eiche bg-bank px-3.5 py-2.5"
          >
            <span className={`flex min-w-0 items-center gap-2.5 ${s.isActive ? '' : 'opacity-45'}`}>
              <span aria-hidden="true">{s.emoji}</span>
              <span className="min-w-0">
                <span className="block truncate text-lampe">{s.name}</span>
                <span className="protokoll normal-case">
                  {s.isAlcoholfree ? 'alkoholfrei' : `${String(s.strength).replace('.', ',')} % vol`}
                </span>
              </span>
            </span>
            <button
              type="button"
              onClick={() => void umschalten(s)}
              className="protokoll shrink-0 normal-case underline decoration-messing/40 underline-offset-4 hover:text-messing"
            >
              {s.isActive ? 'Stilllegen' : 'Reaktivieren'}
            </button>
          </li>
        ))}
      </ul>

      <form onSubmit={anlegen} className="mt-3 flex flex-col gap-2">
        <div className="flex items-stretch gap-2">
          <input
            value={emoji}
            onChange={(e) => setEmoji(e.target.value)}
            className={`${eingabeKlasse} w-16 shrink-0 text-center`}
            aria-label="Emoji"
            maxLength={8}
          />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full min-w-0 ${eingabeKlasse}`}
            placeholder="Sorte"
            maxLength={60}
          />
          <input
            type="number"
            min={0}
            max={100}
            value={staerke}
            onChange={(e) => setStaerke(e.target.value)}
            disabled={alkoholfrei}
            className={`${eingabeKlasse} w-20 shrink-0 text-center tabular-nums disabled:opacity-40`}
            aria-label="Alkoholgehalt in Volumenprozent"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-akte/80">
          <input
            type="checkbox"
            checked={alkoholfrei}
            onChange={(e) => setAlkoholfrei(e.target.checked)}
            className="accent-[#c89b3c]"
          />
          Alkoholfreie Sorte
        </label>
        <Amtsknopf type="submit" disabled={!name.trim() || laeuft}>
          In den Katalog aufnehmen
        </Amtsknopf>
      </form>
      <Fehlermeldung text={fehler} />
    </Abschnitt>
  )
}
